import { Crud } from "@/api";
import { PriceComparisonLine, StripeApi } from "@/api/stripe.api";
import { UserApi } from "@/api/user.api";
import {
  findMissingSourceFiles,
  MissingSourceFile,
} from "@/utils/checkSourceFiles";
import { $checkout } from "@/stores/checkout";
import { $sharedCartSession, setSharedCartSession } from "@/stores/sharedCart";
import { $activeAdminCartId } from "@/stores/adminWorkspace";
import { CheckoutSession, StripeShippingOption } from "@/utils/types/types";
import { LoaderFunctionArgs, redirect } from "react-router-dom";

function transformShippingOptions(options: StripeShippingOption[] | undefined) {
  if (!options || options.length === 0) return [];
  return options.map(el => ({
    value: el.id,
    label: el.metadata?.name || el.display_name,
    display_name: el.display_name,
    price: el.fixed_amount?.amount || 0,
    currency: el.fixed_amount?.currency || "usd",
    originalOption: el,
  }));
}

export function transformCheckoutResponse(resData: {
  session: CheckoutSession;
  shippingOptions: StripeShippingOption[];
  shippingPickupOption: StripeShippingOption;
  shippingOversizedOption: StripeShippingOption | null;
  priceComparison?: PriceComparisonLine[];
}) {
  const transformedShippingOptions = transformShippingOptions(
    resData.shippingOptions
  );

  const transformedPickupOption = resData.shippingPickupOption
    ? {
        value: resData.shippingPickupOption.id,
        label:
          resData.shippingPickupOption.metadata?.name ||
          resData.shippingPickupOption.display_name,
        display_name: resData.shippingPickupOption.display_name,
        price: resData.shippingPickupOption.fixed_amount?.amount || 0,
        currency: resData.shippingPickupOption.fixed_amount?.currency || "usd",
        originalOption: resData.shippingPickupOption,
      }
    : {
        value: "pickup-studio",
        label: "Pickup",
        display_name: "Pickup at Studio",
        price: 0,
        currency: "usd",
      };

  const transformedOversizedOption = resData.shippingOversizedOption
    ? {
        value: resData.shippingOversizedOption.id,
        label:
          resData.shippingOversizedOption.metadata?.name ||
          resData.shippingOversizedOption.display_name,
        display_name: resData.shippingOversizedOption.display_name,
        price: resData.shippingOversizedOption.fixed_amount?.amount || 0,
        currency:
          resData.shippingOversizedOption.fixed_amount?.currency || "usd",
        originalOption: resData.shippingOversizedOption,
      }
    : null;

  return {
    ...resData,
    shippingOptions: transformedShippingOptions,
    shippingPickupOption: transformedPickupOption,
    shippingOversizedOption: transformedOversizedOption,
  };
}

export type CheckoutLoaderCheckoutData = ReturnType<
  typeof transformCheckoutResponse
>;

export default async function checkoutLoader({ request }: LoaderFunctionArgs) {
  try {
    const url = new URL(request.url);
    const params = Object.fromEntries(url.searchParams.entries());
    const urlCartId = params.cartId;
    const urlSecret = params.secret;
    const urlParentOrderNo = params.parentOrderNo;
    const urlCheckoutSessionId = params.checkoutSessionId;

    // Persist a shared link the first time it is opened so that subsequent
    // navigations (e.g. back from the visualizer) don't need the URL params.
    if (urlCartId && urlSecret) {
      setSharedCartSession(urlCartId, urlSecret);
    }

    // Precedence matches CartService.getCart (the sliding cart): URL params
    // → admin workspace cart → stored shared session → last checkout. Before
    // 2026-09-11 the workspace cart was skipped, so an admin who had ever
    // opened a /c/<code> share link in this browser checked out the stored
    // shared cart while the sliding cart showed the workspace cart.
    const activeAdminCartId = urlCartId ? "" : $activeAdminCartId.get();
    const storedSession = activeAdminCartId
      ? { cartId: "", secret: "" }
      : $sharedCartSession.get();
    const cartId =
      urlCartId ||
      activeAdminCartId ||
      storedSession.cartId ||
      $checkout.get().cartId;
    const secret = urlSecret || storedSession.secret;
    // A stored parent-order link is honored ONLY for the cart it arrived
    // with. Before 2026-08-25 the bare persisted value survived across carts
    // and browsers-in-common, so an unrelated customer's checkout could
    // silently inherit it (T-50719_C attached itself to T-50719).
    const storedCheckout = $checkout.get();
    const storedParentOrderNo =
      storedCheckout.parentOrderNo &&
      storedCheckout.parentOrderCartId &&
      storedCheckout.parentOrderCartId === cartId
        ? storedCheckout.parentOrderNo
        : "";
    const parentOrderNo = urlParentOrderNo || storedParentOrderNo;
    const isShared = cartId && secret;

    if (parentOrderNo) {
      $checkout.setKey("parentOrderNo", parentOrderNo);
      $checkout.setKey("parentOrderCartId", cartId || "");
    } else if (storedCheckout.parentOrderNo) {
      // Different cart (or legacy un-scoped value) — drop the stale link.
      $checkout.setKey("parentOrderNo", "");
      $checkout.setKey("parentOrderCartId", "");
    }

    // No cart to check out (e.g. Back after /payment/success cleared the
    // stored cart ids). Same destination the catch below already sends
    // them to, minus the empty-cartId POST that 500s on the backend
    // (Sentry TPW-SERVER-FUNCTIONS-2, P2023). 2026-09-23.
    if (!cartId && !urlCheckoutSessionId) {
      throw redirect("/upload");
    }

    const checkout = urlCheckoutSessionId
      ? await StripeApi.getCheckout(urlCheckoutSessionId).then(res =>
          transformCheckoutResponse(res.data)
        )
      : await StripeApi.checkout({ cartId, parentOrderNo }).then(res =>
          transformCheckoutResponse(res.data)
        );

    const resolvedCartId = cartId || checkout.session?.metadata?.cartId;
    const [cart, user] = await Promise.all([
      isShared
        ? Crud.carts.get(resolvedCartId, { shareSecret: secret })
        : resolvedCartId
          ? Crud.carts.get(resolvedCartId)
          : UserApi.getCart().then(res => res.data.userCart),
      UserApi.getUser(),
    ]);

    // 2026-10-12 (T-51744_B): a shipping-only cart never skips shipping —
    // the backend ignores the flag there; mirror that for any older cart.
    if ((cart as any)?.shippingOnlyForOrderNo)
      (cart as any).skipShipping = false;

    // Extract largest dimensions from cart items for shipping calculation
    const cartDimensions = cart.lineItems.reduce(
      (maxDimensions, item) => {
        const sizeString = item.itemJson?.cartData?.staticField2;

        if (sizeString) {
          let width = 0;
          let height = 0;

          // First, check if there are overall dimensions (for framed items)
          const overallMatch = sizeString.match(
            /Overall Dimensions (\d+\.?\d*)\s*x\s*(\d+\.?\d*)/i
          );
          if (overallMatch) {
            // Use overall dimensions for shipping (includes frame, mat, etc.)
            width = parseFloat(overallMatch[1]);
            height = parseFloat(overallMatch[2]);
          } else {
            // Fallback to image dimensions (for non-framed items)
            const imageMatch = sizeString.match(
              /(\d+\.?\d*)\s*x\s*(\d+\.?\d*)/i
            );
            if (imageMatch) {
              width = parseFloat(imageMatch[1]);
              height = parseFloat(imageMatch[2]);
            }
          }

          if (width > 0 && height > 0) {
            return {
              width: Math.max(maxDimensions.width, width),
              height: Math.max(maxDimensions.height, height),
            };
          }
        }
        return maxDimensions;
      },
      { width: 0, height: 0 }
    );

    // Source-file existence guard (T-50765): catch cart items whose raw/crop
    // objects are gone BEFORE payment. findMissingSourceFiles is fail-open,
    // and this wrapper is belt-and-braces: the guard must never be the reason
    // checkout itself breaks.
    let missingSourceFiles: MissingSourceFile[] = [];
    try {
      missingSourceFiles = await findMissingSourceFiles(cart.lineItems);
    } catch (guardError) {
      console.error("[checkout] source-file guard failed open", guardError);
    }

    return {
      isShared,
      cart,
      checkout,
      user: user.data.user,
      cartDimensions: cartDimensions.width > 0 ? cartDimensions : undefined,
      missingSourceFiles,
    };
  } catch (error) {
    console.error("🚨 Checkout Loader Error:", error);
    throw redirect("/upload");
  }
}
